// 057 — Atención al Adulto Mayor (Fase 7, Tanda 10, ver PLAN.md). Transcripción campo por campo del PDF oficial del
// Anexo 1 MSP: hojas idx 201-202 (footer `SNS-MSP/HCU-form.057/2021`). Informe de brechas en
// `docs/hce-fase7/brechas-121-057-057A-060.md`.
//
// Decisiones ante lo que el papel no resuelve: en E/F/G/J/K cada ítem es una casilla y cada sub-bloque tiene un
// "Describa" de texto (el instructivo pide marcar X y anotar el número/edad en la descripción, así que los ítems
// "No. caídas" o "edad de..." no son campos numéricos aparte); la grilla H son 14 actividades SI/NO sin puntaje
// (tri-estado: sin responder / Sí / No); la fila vacía de la tercera columna de H no se toma. Sin FHIR: el
// Questionnaire anterior tenía ~8 campos sueltos; no hay consumidor demostrable de los linkIds, queda como `Par[]`
// en el PDF firmado.
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import { MARCAS_PRE_DEF, numeroNAInvalidos, OPCIONES_SI_NO_NA, paresDeCampos, sinRenglonesVacios, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import { imcTexto } from './calculosClinicosDatos';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });
const texto = (clave: string, etiqueta: string, tipo?: CampoSimple['tipo']): CampoSimple => ({ clave, etiqueta, ...(tipo ? { tipo } : {}) });
/** Casillas numeradas como en el papel ("1 Dismovilidad"...) más su "Describa". */
const numeradas = (prefijo: string, etiquetas: readonly string[], descripcion: string): readonly CampoSimple[] => [
  ...etiquetas.map((e, i) => casilla(`${prefijo}${i + 1}`, `${i + 1}. ${e}`)),
  area(`${prefijo}Describa`, descripcion),
];

// ---------- B. Entorno social ----------

export const CAMPOS_ENTORNO_057: readonly CampoSimple[] = [
  casilla('viveSolo', 'Vive solo'),
  casilla('viveAcompanado', 'Vive acompañado'),
  texto('conQuien', 'Con quién'),
  casilla('viveCalle', 'Vive en la calle'),
  texto('hijosVivos', 'No. hijos vivos', 'numeroNA'),
  texto('estadoCivil', 'Estado civil'),
  casilla('institucionalizado', 'Institucionalizado'),
  texto('institucion', 'Institucionalizado — nombre'),
  casilla('trabaja', 'Trabaja'),
  casilla('jubilado', 'Jubilado'),
  casilla('recibeBono', 'Recibe bono'),
  casilla('otrosIngresos', 'Otros ingresos'),
];

// ---------- C. Motivo de consulta ----------

export const CAMPOS_MOTIVO_057: readonly CampoSimple[] = [
  area('motivo', 'Motivo de consulta'),
  casilla('informantePaciente', 'Informante — paciente'),
  casilla('informanteCuidador', 'Informante — cuidador'),
  casilla('informanteOtros', 'Informante — otros'),
];

// ---------- D. Enfermedad actual ----------

export const CAMPOS_ENFERMEDAD_057: readonly CampoSimple[] = [area('enfermedad', 'Enfermedad actual')];

// ---------- E. Antecedentes personales ("describir anotando el número") ----------

export const CAMPOS_ALERTAS_057 = numeradas(
  'al',
  ['No. de caídas en un año', 'No. de caídas en el último mes', 'Dismovilidad', 'Pérdida de peso', 'Astenia', 'Pérdida de apetito', 'Problemas de deglución', 'Desorientación', 'Pérdida de memoria', 'Alteraciones del ánimo', 'Alteraciones del sueño'],
  'Describa',
);
export const CAMPOS_GENERALES_057 = numeradas('ge', ['Inmunizaciones', 'Higiene general', 'Higiene oral', 'Ejercicio', 'Alimentación', 'Actividad recreativa', 'Controles de salud', 'Alergias', 'Otros'], 'Describa');
export const CAMPOS_HABITOS_057 = numeradas('ha', ['Tabaquismo', 'Alcoholismo', 'Adicciones', 'Otro hábito'], 'Describa');
export const CAMPOS_CLINICOS_057 = numeradas(
  'cl',
  ['Dermatológicos', 'Visuales', 'Otorrino', 'Odontológicos', 'Endocrinos', 'Cardiovasculares', 'Respiratorios', 'Digestivos', 'Neurológicos', 'Urológicos', 'Hemolinfáticos', 'Infecciosos', 'Oncológicos', 'Músculo esqueléticos', 'Psiquiátricos', 'Ginecológicos', 'Nefrológicos', 'Hospitalización en el último año'],
  'Describa',
);
export const CAMPOS_GINECO_057 = numeradas('gi', ['Edad de menopausia', 'Edad de última mamografía', 'Edad de última citología', 'Embarazos', 'Partos', 'Cesáreas', 'Terapia hormonal'], 'Describa');
export const CAMPOS_ANDRO_057 = numeradas('an', ['Edad del último antígeno prostático', 'Terapia hormonal'], 'Describa');

// ---------- F. Antecedentes farmacológicos / G. Antecedentes familiares ----------

export const CAMPOS_FARMACOLOGICOS_057 = numeradas('fa', ['AINES', 'Analgésicos', 'Antidiabéticos', 'Antihipertensivos', 'Anticoagulantes', 'Psicofármacos', 'Antibióticos', 'Otros', 'Automedicación'], 'Describa');
export const CAMPOS_FAMILIARES_057 = numeradas(
  'fm',
  ['Cardiopatía', 'Diabetes', 'Hipertensión arterial', 'Neoplasia', 'Alzheimer', 'Parkinson', 'Tuberculosis', 'Violencia intrafamiliar', 'Síndrome del cuidador cansado', 'Otros'],
  'Describa',
);

// ---------- H. Situación funcional (SI/NO por actividad) ----------

const actividad = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones: OPCIONES_SI_NO_NA });
export const CAMPOS_BASICAS_057: readonly CampoSimple[] = [
  actividad('banaSolo', 'Se baña solo'),
  actividad('vistePropio', 'Se viste y desviste solo'),
  actividad('apariencia', 'Cuida su apariencia personal solo'),
  actividad('inodoro', 'Utiliza el inodoro solo'),
  actividad('esfinteres', 'Controla esfínteres'),
  actividad('traslada', 'Se traslada, se acuesta y se levanta solo'),
  actividad('camina', 'Camina solo'),
  actividad('alimenta', 'Se alimenta solo'),
];
export const CAMPOS_INSTRUMENTALES_057: readonly CampoSimple[] = [
  actividad('telefono', 'Usa el teléfono'),
  actividad('transporte', 'Usa medios de transporte'),
  actividad('compras', 'Va de compras'),
  actividad('comida', 'Prepara la comida'),
  actividad('medicamentos', 'Controla sus medicamentos'),
  actividad('dinero', 'Maneja dinero'),
];

// ---------- I. Constantes vitales y antropometría ----------

export const CAMPOS_CONSTANTES_057: readonly CampoSimple[] = [
  texto('paAcostado', 'Presión arterial acostado'),
  texto('paSentado', 'Presión arterial sentado'),
  texto('pulsioximetria', 'Pulsioximetría'),
  texto('temperatura', 'Temperatura', 'numeroNA'),
  texto('pulso', 'Pulso/min', 'numeroNA'),
  texto('fr', 'Frecuencia respiratoria', 'numeroNA'),
  texto('peso', 'Peso (kg)', 'numeroNA'),
  texto('talla', 'Talla (cm)', 'numeroNA'),
  // IMC calculado desde peso y talla (solo lectura); el papel lo deriva de esas dos casillas.
  { ...texto('imc', 'IMC', 'numeroNA'), calculado: (v) => imcTexto(v.peso, v.talla) },
  texto('perimetroAbdominal', 'Perímetro abdominal', 'numeroNA'),
  texto('perimetroBraquial', 'Perímetro braquial', 'numeroNA'),
  texto('perimetroPantorrilla', 'Perímetro de pantorrilla', 'numeroNA'),
];

// ---------- J. Revisión actual de órganos y sistemas / K. Examen físico ("marcar X cuando presente patología") ----------

export const CAMPOS_REVISION_057 = numeradas('re', ['Visión', 'Audición', 'Olfato y gusto', 'Respiratorio', 'Cardiovascular', 'Digestivo', 'Genitourinario', 'Músculo esquelético', 'Endocrino', 'Hemolinfático', 'Nervioso', 'Piel'], 'Describa');
export const CAMPOS_EXAMEN_057 = numeradas(
  'ex',
  ['Piel - anexos', 'Cabeza', 'Ojos', 'Oídos', 'Boca', 'Nariz', 'Cuello', 'Axilas - mamas', 'Tórax', 'Abdomen', 'Columna', 'Pelvis', 'Miembros superiores', 'Miembros inferiores', 'Neurológico'],
  'Describa',
);

// ---------- L. Diagnósticos y síndromes geriátricos ----------

export const NUM_DIAGNOSTICOS_057 = 6;
export const CAMPOS_SINDROMES_057: readonly CampoSimple[] = [
  ['Fragilidad', 'Depresión', 'Delirium', 'Úlceras por presión', 'Incontinencia', 'Estreñimiento', 'Dismovilidad', 'Caída', 'Mal nutrición', 'Demencia', 'Síndrome del cuidador cansado'],
].flatMap((l) => l.map((e, i) => casilla(`sg${i + 1}`, e)));

// ---------- M. Pruebas diagnósticas / N. Tratamiento ----------

export const CAMPOS_PRUEBAS_057: readonly CampoSimple[] = [area('pruebas', 'Pruebas diagnósticas')];
export const CAMPOS_TRATAMIENTO_057: readonly CampoSimple[] = [area('tratamiento', 'Tratamiento')];

export interface Estado057 {
  readonly entorno: Valores;
  readonly motivo: Valores;
  readonly enfermedad: Valores;
  readonly alertas: Valores;
  readonly generales: Valores;
  readonly habitos: Valores;
  readonly clinicos: Valores;
  readonly gineco: Valores;
  readonly andro: Valores;
  readonly farmacologicos: Valores;
  readonly familiares: Valores;
  readonly basicas: Valores;
  readonly instrumentales: Valores;
  readonly constantes: Valores;
  readonly revision: Valores;
  readonly examen: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly sindromes: Valores;
  readonly pruebas: Valores;
  readonly tratamiento: Valores;
}

export function estadoInicial057(): Estado057 {
  return {
    entorno: valoresVacios(CAMPOS_ENTORNO_057),
    motivo: valoresVacios(CAMPOS_MOTIVO_057),
    enfermedad: valoresVacios(CAMPOS_ENFERMEDAD_057),
    alertas: valoresVacios(CAMPOS_ALERTAS_057),
    generales: valoresVacios(CAMPOS_GENERALES_057),
    habitos: valoresVacios(CAMPOS_HABITOS_057),
    clinicos: valoresVacios(CAMPOS_CLINICOS_057),
    gineco: valoresVacios(CAMPOS_GINECO_057),
    andro: valoresVacios(CAMPOS_ANDRO_057),
    farmacologicos: valoresVacios(CAMPOS_FARMACOLOGICOS_057),
    familiares: valoresVacios(CAMPOS_FAMILIARES_057),
    basicas: valoresVacios(CAMPOS_BASICAS_057),
    instrumentales: valoresVacios(CAMPOS_INSTRUMENTALES_057),
    constantes: valoresVacios(CAMPOS_CONSTANTES_057),
    revision: valoresVacios(CAMPOS_REVISION_057),
    examen: valoresVacios(CAMPOS_EXAMEN_057),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_057 }, () => diagnosticoConMarcaVacio()),
    sindromes: valoresVacios(CAMPOS_SINDROMES_057),
    pruebas: valoresVacios(CAMPOS_PRUEBAS_057),
    tratamiento: valoresVacios(CAMPOS_TRATAMIENTO_057),
  };
}

/** Los renglones "N." que el usuario dejó sin descripción (los agrega `vincularDescriba` al marcar) no salen en el PDF. */
const limpios = (v: Valores): Valores => Object.fromEntries(Object.entries(v).map(([k, x]) => [k, k.endsWith('Describa') ? sinRenglonesVacios(x) : x]));

/** Campos `numeroNA` de 057 con un valor inválido (ni número ni NA). */
export const numerosInvalidos057 = (e: Estado057): string[] => [...numeroNAInvalidos(CAMPOS_ENTORNO_057, e.entorno), ...numeroNAInvalidos(CAMPOS_CONSTANTES_057, e.constantes)];

export function paresDe057(estado: Estado057): Par[] {
  const e: Estado057 = { ...estado, alertas: limpios(estado.alertas), generales: limpios(estado.generales), habitos: limpios(estado.habitos), clinicos: limpios(estado.clinicos), gineco: limpios(estado.gineco), andro: limpios(estado.andro), farmacologicos: limpios(estado.farmacologicos), familiares: limpios(estado.familiares), revision: limpios(estado.revision), examen: limpios(estado.examen) };
  return [
    ...paresDeCampos(CAMPOS_ENTORNO_057, e.entorno, 'Entorno social — '),
    ...paresDeCampos(CAMPOS_MOTIVO_057, e.motivo),
    ...paresDeCampos(CAMPOS_ENFERMEDAD_057, e.enfermedad),
    ...paresDeCampos(CAMPOS_ALERTAS_057, e.alertas, 'Alertas de riesgo — '),
    ...paresDeCampos(CAMPOS_GENERALES_057, e.generales, 'Antecedentes generales — '),
    ...paresDeCampos(CAMPOS_HABITOS_057, e.habitos, 'Hábitos nocivos — '),
    ...paresDeCampos(CAMPOS_CLINICOS_057, e.clinicos, 'Antecedentes clínico quirúrgicos — '),
    ...paresDeCampos(CAMPOS_GINECO_057, e.gineco, 'Gineco-obstétricos — '),
    ...paresDeCampos(CAMPOS_ANDRO_057, e.andro, 'Andrológicos — '),
    ...paresDeCampos(CAMPOS_FARMACOLOGICOS_057, e.farmacologicos, 'Antecedentes farmacológicos — '),
    ...paresDeCampos(CAMPOS_FAMILIARES_057, e.familiares, 'Antecedentes familiares — '),
    ...paresDeCampos(CAMPOS_BASICAS_057, e.basicas, 'Actividades básicas de la vida diaria — '),
    ...paresDeCampos(CAMPOS_INSTRUMENTALES_057, e.instrumentales, 'Actividades instrumentales — '),
    ...paresDeCampos(CAMPOS_CONSTANTES_057, e.constantes),
    ...paresDeCampos(CAMPOS_REVISION_057, e.revision, 'Revisión de órganos y sistemas — '),
    ...paresDeCampos(CAMPOS_EXAMEN_057, e.examen, 'Examen físico — '),
    ...e.diagnosticos.map((d, i) => ({
      pregunta: `Diagnóstico ${i + 1}`,
      respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
    })),
    ...paresDeCampos(CAMPOS_SINDROMES_057, e.sindromes, 'Síndromes geriátricos — '),
    ...paresDeCampos(CAMPOS_PRUEBAS_057, e.pruebas),
    ...paresDeCampos(CAMPOS_TRATAMIENTO_057, e.tratamiento),
  ].filter((p) => p.respuesta.trim() !== '');
}
